/* eslint-disable max-lines -- Pre-existing violation */
import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus} from 'platform/components';
import {Show} from 'platform/foundation';

import {isNilOrEmpty, isNotNil, isPositive} from 'ramda-adjunct';

import {useAssistantEntityContext} from '@dms/ai-assistant';
import {
  useGetParticipationQuery,
  useGetTireSetCountQuery,
  useGetTireVehicleCountQuery,
} from '@dms/api/core';
import {useGetVehicleCustomerQuery} from '@dms/api/customer';
import {useGetConfigurationByVehicleQuery} from '@dms/api/new-vehicle';
import {useGetBusinessCaseQuery, useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useGetRentalVehicleQuery,
  useGetSaleVehicleByVehicleQuery,
  useGetSaleVehicleQuery,
  useGetVehicleComplaintsQuery,
  useGetVehicleRelatedRecordCountQuery,
  useVehicleActiveReservationQuery,
} from '@dms/api/vehicle';
import {useGetVehicleWarehouseManagementSettingsQuery} from '@dms/api/warehouse';
import {
  useGetPostponeJobsByVehicleIdQuery,
  useGetServiceCaseByVehicleCountQuery,
  useGetServiceVehicleQuery,
  useGetVehicleServiceCampaignQuery,
} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import {CustomNoticeList, useCustomNoticesBadge} from '@dms/features/custom-notices';
import {TaskGridPage, useTasksCount} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {
  ErrorPage,
  NavigationItem,
  OwnershipSection,
  is404ApiError,
  useIsLicenceEnabled,
  usePricePermissions,
  usePermissions,
  useVehiclePhotosPermissions,
  VehicleWarehouseProvider,
  useChecklist,
  useChecklistBadge,
  toMenuBadge,
  VehicleActiveServiceCases,
  VehicleHistoryOfRepairs,
  VehiclePostponedJobs,
  Page,
  Main,
} from '@dms/shared';
import {VehiclePhotosRoutes} from '@dms/teas';
import {VehicleAuction} from '@dms/vehicle-auction';
import {RentalVehicleRentalOrders} from '@dms/vehicle-detail';

import {buildArray, buildObject, composePath, useRequiredParams} from 'shared/web';

import {getVehicleAssistantEntities} from '../../utils/getVehicleAssistantEntities';
import {Advertisements} from './(sections)/Advertisements/Advertisements';
import {BusinessCases} from './(sections)/BusinessCases/BusinessCases';
import {Campaigns} from './(sections)/Campaigns/Campaigns';
import {CarisServiceRequests} from './(sections)/CarisServiceRequests/CarisServiceRequests';
import {Claims} from './(sections)/Claims/Claims';
import {Customers} from './(sections)/Customers/Customers';
import {Documents} from './(sections)/Documents/Documents';
import {Insights} from './(sections)/Insights/Insights';
import {Interests} from './(sections)/Interests/Interests';
import {NewVehicle} from './(sections)/NewVehicle/NewVehicle';
import {InspectionPhotos} from './(sections)/Photos/InspectionPhotos';
import {VehiclePhotos} from './(sections)/Photos/VehiclePhotos';
import {PricesAndFinances} from './(sections)/PricesAndFinances/PricesAndFinances';
import {Reservations} from './(sections)/Reservations/Reservations';
import {SalesCampaigns} from './(sections)/SalesCampaigns/SalesCampaigns';
import {TestDrives} from './(sections)/TestDrives/TestDrives';
import {TestDrivesV2} from './(sections)/TestDrives/TestDrivesV2';
import {ThreeHundredAndSixtyPhotos} from './(sections)/ThreeHundredAndSixtyPhotos/ThreeHundredAndSixtyPhotos';
import {Tires} from './(sections)/Tires/Tires';
import {VehicleActivityLogs} from './(sections)/VehicleActivityLogs/VehicleActivityLogs';
import {VehicleChecklists} from './(sections)/VehicleChecklists/VehicleChecklists';
import {Conditions} from './(sections)/VehicleCondition/Conditions';
import {InspectionDetail} from './(sections)/VehicleCondition/InspectionDetail';
import {Inspections} from './(sections)/VehicleCondition/Inspections';
import {VehicleDetails} from './(sections)/VehicleDetails/VehicleDetails';
import {VehicleWarehouseInfo} from './(sections)/WarehouseInfo/VehicleWarehouseInfo';
import {VehicleActivityPanel} from './components/VehicleActivityPanel/VehicleActivityPanel';
import {VehicleHeader} from './components/VehicleHeader';

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
export function VehicleDetail() {
  const {id} = useRequiredParams();

  const {data: relatedRecordCount} = useGetVehicleRelatedRecordCountQuery({vehicleId: id});

  const {count: tasksTodoCount} = useTasksCount({
    entityId: id,
    relation: EntityResourceIds.vehicle,
    state: 'TODO',
  });
  const {count: tasksInProgressCount} = useTasksCount({
    entityId: id,
    relation: EntityResourceIds.vehicle,
    state: 'IN_PROGRESS',
  });
  const tasksCountBadge = tasksTodoCount + tasksInProgressCount;

  const {customNoticesBadge} = useCustomNoticesBadge({
    resourceId: EntityResourceIds.vehicle,
    recordId: id,
  });

  const {
    data: vehicleData,
    isLoading: isVehicleLoading,
    isError: isVehicleErrored,
    error: vehicleError,
  } = useGetVehicleQuery({vehicleId: id});

  const maximization = {
    enabled: true,
    title: `${i18n.t('entity.customerMessage.labels.contextResource.VEHICLE')} ${vehicleData?.title ?? ''}`,
  };

  const {data: allInStockSaleVehicles} = useGetSaleVehicleByVehicleQuery({
    vehicleId: id,
    type: 'IN_STOCK',
  });

  const {data: saleVehicle, isError: isSaleVehicleError} = useGetSaleVehicleQuery({
    vehicleId: id,
  });
  const {ownerRef: assistantEntitiesOwnerRef, synchronizeRef: synchronizeAssistantEntitiesRef} =
    useAssistantEntityContext(getVehicleAssistantEntities(id, vehicleData, saleVehicle));

  const {data: rentalVehicle, isError: isRentalVehicleError} = useGetRentalVehicleQuery({
    vehicleId: id,
  });

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: id,
  });

  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.businessCase,
      recordId: saleVehicle?.saleBusinessCaseId ?? '',
    },
    {skip: isNilOrEmpty(saleVehicle?.saleBusinessCaseId)}
  );

  const {data: businessCase} = useGetBusinessCaseQuery(
    {businessCaseId: saleVehicle?.saleBusinessCaseId ?? ''},
    {skip: isNilOrEmpty(saleVehicle?.saleBusinessCaseId)}
  );

  const [
    hasComplaintReadPermission,
    hasReadBusinessCasePurchasePermission,
    hasReadBusinessCaseSellingPermission,
    hasVehicleReadActiveReservationPermission,
    hasVehicleReadReservationHistoryPermission,
    hasVehicleReadWarehouseInfoPermission,
    canReadBusinessCase,
    canReadRentalVehicle,
    hasReadAdvertisingSettingsPermission,
    canReadServiceCase,
    canReadDocuments,
    canReadInterest,
    canReadPneuhotel,
    hasReadTestDrivePermission,
    canReadNewVehicleConfiguration,
    canReadRentalOrder,
    canReadCarisServiceRequests,
  ] = usePermissions({
    permissionKeys: [
      'complaintRead',
      'viewBusinessCasePurchase',
      'viewBusinessCaseSelling',
      'vehicleReadActiveReservation',
      'vehicleReadReservationHistory',
      'vehicleReadWarehouseInformation',
      'businessCaseRead',
      'readRentalVehicle',
      'readAdvertisingSettings',
      'serviceCaseRead',
      'readDocuments',
      'interestRead',
      'pneuhotelRead',
      'readTestDrive',
      'newVehicleConfigurationRead',
      'readRentalOrder',
      'readCarisServiceRequests',
    ],
    scopes: {
      serviceCaseRead: {
        // We want to show DG without scopes, BE handles DG ACL
        branchId: 'SKIP',
        // We want to show DG without scopes, BE handles DG ACL
        serviceCaseType: 'SKIP',
      },
      complaintRead: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      vehicleReadActiveReservation: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      vehicleReadReservationHistory: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      readAdvertisingSettings: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      viewBusinessCasePurchase: {
        // BC tab should be allowed regardless of participation scope
        participation: 'SKIP',
        // BC tab should be allowed regardless of branch scope
        branchId: 'SKIP',
      },
      viewBusinessCaseSelling: {
        // BC tab should be allowed regardless of participation scope
        participation: 'SKIP',
        // BC tab should be allowed regardless of branch scope
        branchId: 'SKIP',
      },
      interestRead: {
        // interest tab should be allowed regardless of participation scope
        participation: 'SKIP',
        // interest tab should be allowed regardless of branch scope
        branchId: 'SKIP',
      },
      readRentalOrder: {
        // there is no specific rental order record in the vehicle detail context, BE handles DG ACL
        participation: 'SKIP',
        // there is no specific rental order record in the vehicle detail context, BE handles DG ACL
        branchId: 'SKIP',
      },
    },
  });

  const {isWorkshopModuleEnabled} = useIsLicenceEnabled();
  const isTireInventoryEnabled = canReadPneuhotel;

  const {data: serviceVehicle, isError: isServiceVehicleError} = useGetServiceVehicleQuery(
    {vehicleId: id},
    {skip: !isWorkshopModuleEnabled}
  );

  const {canReadVehicleAlbumPhotos} = useVehiclePhotosPermissions({
    vehicleId: id,
  });

  const {data: vehicleServiceCampaign} = useGetVehicleServiceCampaignQuery(
    {vehicleId: id},
    {skip: !isWorkshopModuleEnabled}
  );
  const {data: postopnedJobsData} = useGetPostponeJobsByVehicleIdQuery(
    {vehicleId: id},
    {skip: !isWorkshopModuleEnabled}
  );
  const activeServiceCasesCountApiArg =
    isWorkshopModuleEnabled && isNotNil(serviceVehicle?.serviceVehicleId)
      ? {vehicleId: id, status: 'ACTIVE' as const}
      : skipToken;
  const finishedServiceCasesCountApiArg =
    isWorkshopModuleEnabled && isNotNil(serviceVehicle?.serviceVehicleId)
      ? {vehicleId: id, status: 'FINISHED' as const}
      : skipToken;
  const {data: activeServiceCasesCount} = useGetServiceCaseByVehicleCountQuery(
    activeServiceCasesCountApiArg
  );
  const {data: finishedServiceCasesCount} = useGetServiceCaseByVehicleCountQuery(
    finishedServiceCasesCountApiArg
  );
  const {data: vehicleComplaints} = useGetVehicleComplaintsQuery({vehicleId: id});
  const {data: warehouseSettings} = useGetVehicleWarehouseManagementSettingsQuery();
  const {data: vehicleTires} = useGetTireVehicleCountQuery(
    {vehicleId: id},
    {skip: !isTireInventoryEnabled}
  );

  const isSalesModuleEnabled = canReadBusinessCase;
  const isRentalVehicleModuleEnabled = canReadRentalVehicle;

  const {canViewAnyOfVehiclePriceFields: canReadFinances} = usePricePermissions({
    vehicleRecordId: id,
    businessCaseRecordId: saleVehicle?.saleBusinessCaseId,
  });

  const activeServiceCampaignsLength =
    vehicleServiceCampaign?.serviceCampaign?.filter(
      (campaign) => campaign.kind === 'ACTIVE_CAMPAIGN'
    ).length ?? 0;

  const isSaleVehicle = isNotNil(saleVehicle) && !isSaleVehicleError;
  const isServiceVehicle = isNotNil(serviceVehicle) && !isServiceVehicleError;
  const isRentalVehicle = isNotNil(rentalVehicle) && !isRentalVehicleError;
  const hasVehiclePhotosTab = canReadVehicleAlbumPhotos && (isSaleVehicle || isServiceVehicle);

  const {progress: saleVehicleProgress} = useChecklist({
    recordId: saleVehicle?.id || '',
    resourceId: 'SALE_VEHICLE',
  });

  const {progress: serviceVehicleProgress} = useChecklist({
    recordId: serviceVehicle?.serviceVehicleId || '',
    resourceId: 'SERVICE_VEHICLE',
  });

  const {badgeSettings} = useChecklistBadge({
    progress: [saleVehicleProgress, serviceVehicleProgress],
  });

  const {data: vehicleTireSets} = useGetTireSetCountQuery(
    {vehicleId: id ?? '', states: ['INSTOCK']},
    {skip: !isServiceVehicle}
  );

  const {data: vehicleCustomers} = useGetVehicleCustomerQuery(
    {vehicleId: id},
    {skip: !isServiceVehicle}
  );

  const {
    error: vehicleReservationError,
    isError: isVehicleReservationError,
    status: vehicleReservationStatus,
  } = useVehicleActiveReservationQuery({
    vehicleId: id,
  });

  const isNewVehicleEnabled = isFeatureEnabled(featureFlags.NEW_VEHICLE_CATALOGUE);
  const {currentData: newVehicleConfiguration, isError: isNewVehicleConfigurationError} =
    useGetConfigurationByVehicleQuery(
      {vehicleId: id},
      {skip: !isNewVehicleEnabled || !canReadNewVehicleConfiguration}
    );

  const navigationItems = buildArray<NavigationItem>()
    .when(isSaleVehicle, {
      id: 'insights',
      label: i18n.t('entity.interest.labels.overviewTab'),
      href: composePath(vehiclesRoutes.insights, {params: {id}}),
      content: <Insights vehicleId={id} />,
      'data-testid': testIds.vehicles.detail('navigation-item-insights'),
      hasSeparator: true,
    })
    .add({
      id: 'details',
      label: i18n.t('entity.vehicle.labels.vehicleDetails'),
      href: composePath(vehiclesRoutes.detail, {params: {id}}),
      content: [
        {route: vehiclesRoutes.detail, element: <VehicleDetails maximization={maximization} />},
        {
          route: vehiclesRoutes.vehicleData,
          element: <VehicleDetails maximization={maximization} />,
        },
        {route: vehiclesRoutes.features, element: <VehicleDetails maximization={maximization} />},
        {
          route: vehiclesRoutes.serviceIntervals,
          element: <VehicleDetails maximization={maximization} />,
        },
        {
          route: vehiclesRoutes.additionalInformation,
          element: <VehicleDetails maximization={maximization} />,
        },
        {route: vehiclesRoutes.rental, element: <VehicleDetails maximization={maximization} />},
      ],
      'data-testid': testIds.vehicles.detail('navigation-item-details'),
    })
    .when(isSalesModuleEnabled || isRentalVehicleModuleEnabled || isWorkshopModuleEnabled, {
      id: 'conditions',
      label: i18n.t('entity.vehicle.labels.vehicleCondition'),
      href: composePath(
        isSaleVehicle ? vehiclesRoutes.conditionDetail : vehiclesRoutes.conditionsInspections,
        {
          params: {
            id,
          },
        }
      ),
      content: [
        {route: vehiclesRoutes.conditionsInspections, element: <Inspections />},
        {route: vehiclesRoutes.inspectionDetail, element: <InspectionDetail />},
        {route: vehiclesRoutes.inspectionDetailTab, element: <InspectionDetail />},
        {route: vehiclesRoutes.conditions, element: <Conditions />},
        {route: vehiclesRoutes.conditionDetail, element: <Conditions />},
        {route: vehiclesRoutes.conditionDetailTab, element: <Conditions />},
      ],
      'data-testid': testIds.vehicles.detail('navigation-item-conditions'),
    })
    .when(
      isSaleVehicle &&
        warehouseSettings?.isEnabled &&
        hasVehicleReadWarehouseInfoPermission &&
        isSalesModuleEnabled,
      {
        id: 'vehicleWarehouses',
        label: i18n.t('entity.vehicle.labels.vehicleWarehouseInfo'),
        href: composePath(vehiclesRoutes.vehicleWarehouseInfo, {params: {id}}),
        content: [
          {
            route: vehiclesRoutes.vehicleWarehouseInfo,
            element: (
              <VehicleWarehouseInfo
                maximization={maximization}
                vehicleId={id}
                data-testid={testIds.vehicles.vehicleWarehouseInfo()}
              />
            ),
          },
        ],
        isDisabled: (allInStockSaleVehicles?.saleVehicleIds ?? []).length < 1,
      }
    )
    .when(
      ((isSaleVehicle && isSalesModuleEnabled) ||
        (isRentalVehicle && isRentalVehicleModuleEnabled)) &&
        canReadFinances,
      {
        id: 'finances',
        label: i18n.t('entity.vehicle.labels.finances'),
        href: composePath(vehiclesRoutes.finances, {params: {id}}),
        content: [
          {
            route: vehiclesRoutes.finances,
            element: <PricesAndFinances maximization={maximization} />,
          },
          {
            route: vehiclesRoutes.financesPrices,
            element: <PricesAndFinances maximization={maximization} />,
          },
          {
            route: vehiclesRoutes.financesFinancingLeasing,
            element: <PricesAndFinances maximization={maximization} />,
          },
          {
            route: vehiclesRoutes.financesEarningsCosts,
            element: <PricesAndFinances maximization={maximization} />,
          },
          {
            route: vehiclesRoutes.financesRentalPrices,
            element: <PricesAndFinances maximization={maximization} />,
          },
        ],
        'data-testid': testIds.vehicles.detail('navigation-item-finances'),
      }
    )
    .when(isSalesModuleEnabled && isSaleVehicle && hasReadAdvertisingSettingsPermission, {
      id: 'advertisements',
      label: i18n.t('entity.vehicle.labels.advertisements'),
      href: composePath(vehiclesRoutes.publishing, {params: {id}}),
      content: [{route: vehiclesRoutes.publishing, element: <Advertisements />}],
      'data-testid': testIds.vehicles.detail('navigation-item-advertisements'),
    })
    .when(isFeatureEnabled(featureFlags.AUCTIONS) && isSalesModuleEnabled && isSaleVehicle, {
      id: 'auction',
      label: i18n.t('entity.auction.labels.auction'),
      href: composePath(vehiclesRoutes.auction, {params: {id}}),
      content: [
        {
          route: vehiclesRoutes.auction,
          element: (
            <Main data-testid={testIds.vehicles.detail('auction')}>
              <VehicleAuction vehicleId={id} />
            </Main>
          ),
        },
      ],
      'data-testid': testIds.vehicles.detail('navigation-item-auction'),
    })
    .when(isFeatureEnabled(featureFlags.CAMPAIGNS) && isSalesModuleEnabled && isSaleVehicle, {
      id: 'campaigns',
      label: i18n.t('entity.salesCampaign.labels.title'),
      href: composePath(vehiclesRoutes.campaigns, {params: {id}}),
      content: [{route: vehiclesRoutes.campaigns, element: <SalesCampaigns />}],
      badge: toMenuBadge(relatedRecordCount?.campaigns ?? 0),
      'data-testid': testIds.vehicles.detail('navigation-item-campaigns'),
    })
    .when(isSalesModuleEnabled || isRentalVehicleModuleEnabled || isWorkshopModuleEnabled, {
      id: 'photos',
      label: i18n.t('entity.photo.labels.photos'),
      href: composePath(vehiclesRoutes.photos, {
        params: {
          id,
          tab: hasVehiclePhotosTab
            ? VehiclePhotosRoutes.vehiclePhotos
            : VehiclePhotosRoutes.condition,
        },
      }),
      content: [
        {route: vehiclesRoutes.photos, element: <VehiclePhotos />},
        {route: vehiclesRoutes.photoByInspectionId, element: <InspectionPhotos />},
      ],
      'data-testid': testIds.vehicles.detail('navigation-item-photos'),
      hasSeparator:
        !canReadDocuments && !isFeatureEnabled(featureFlags.VEHICLE_THREE_HUNDRED_AND_SIXTY_PHOTOS),
    })
    .whenFeatureEnabled(featureFlags.VEHICLE_THREE_HUNDRED_AND_SIXTY_PHOTOS, {
      id: 'threeHundredAndSixtyPhotos',
      label: i18n.t('entity.vehicle.labels.threeHundredAndSixtyPhotos'),
      href: composePath(vehiclesRoutes.threeHundredAndSixtyPhotos, {params: {id}}),
      content: <ThreeHundredAndSixtyPhotos />,
      'data-testid': testIds.vehicles.detail('navigation-item-threeHundredAndSixtyPhotos'),
      hasSeparator: !canReadDocuments,
    })
    .when(canReadDocuments, {
      id: 'documents',
      label: i18n.t('page.vehicle.documents.title'),
      href: composePath(vehiclesRoutes.documents, {params: {id}}),
      content: <Documents maximization={maximization} />,
      badge: toMenuBadge(relatedRecordCount?.documents ?? 0),
      'data-testid': testIds.vehicles.detail('navigation-item-documents'),
      hasSeparator: true,
    })
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
      id: 'checklists',
      label: i18n.t('entity.checklist.labels.title'),
      href: composePath(vehiclesRoutes.checklists, {params: {id}}),
      content: (
        <VehicleChecklists
          saleVehicleId={saleVehicle?.id || ''}
          serviceVehicleId={serviceVehicle?.serviceVehicleId || ''}
          vehicleId={id}
        />
      ),
      badge: badgeSettings,
      'data-testid': testIds.vehicles.detail('navigation-item-checklists'),
      hasSeparator: false,
    })
    .add({
      id: 'tasks',
      label: i18n.t('entity.task.labels.tasks'),
      href: composePath(vehiclesRoutes.tasks, {params: {id}}),
      'data-testid': testIds.vehicles.detail('navigation-item-tasks'),
      badge: {
        colorScheme: 'neutral',
        value: tasksCountBadge,
      },
      content: (
        <TaskGridPage
          maximization={maximization}
          gridCode="vehicle-task"
          entityId={id}
          data-testid={testIds.vehicles.detail('tasks')}
        />
      ),
      hasSeparator: !isFeatureEnabled(featureFlags.CUSTOM_NOTICES),
    })
    .whenFeatureEnabled(featureFlags.CUSTOM_NOTICES, {
      id: 'customNotices',
      label: i18n.t('general.customNotices.labels.title'),
      href: composePath(vehiclesRoutes.customNotices, {params: {id}}),
      content: (
        <CustomNoticeList
          resourceId={EntityResourceIds.vehicle}
          recordId={id}
          data-testid={testIds.vehicles.detail('customNotices')}
        />
      ),
      badge: customNoticesBadge,
      hasSeparator: true,
      'data-testid': testIds.vehicles.detail('navigation-item-customNotices'),
    })
    .when(isSaleVehicle, {
      id: 'customers',
      label: i18n.t('page.customers.title'),
      href: composePath(vehiclesRoutes.customers, {params: {id}}),
      content: <Customers maximization={maximization} />,
      'data-testid': testIds.vehicles.detail('navigation-item-customers'),
    })
    .when(
      isNewVehicleEnabled &&
        canReadNewVehicleConfiguration &&
        (isNotNil(newVehicleConfiguration) || isNewVehicleConfigurationError),
      {
        id: 'new-vehicle',
        label: i18n.t('entity.newVehicle.labels.newVehicle', {
          defaultValue: 'New vehicle',
        }),
        href: composePath(vehiclesRoutes.newVehicleDetail, {params: {id}}),
        content: [
          vehiclesRoutes.newVehicleDetail,
          vehiclesRoutes.newVehicleConfiguration,
          vehiclesRoutes.newVehiclePriceBasis,
          vehiclesRoutes.newVehicleCustomFields,
        ].map((route) => ({route, element: <NewVehicle vehicleId={id} />})),
        'data-testid': testIds.vehicles.detail('navigation-item-new-vehicle'),
      }
    )
    .when(isSaleVehicle && canReadInterest && (isSalesModuleEnabled || isWorkshopModuleEnabled), {
      id: 'interests',
      label: i18n.t('page.interests.title'),
      href: composePath(vehiclesRoutes.interests, {params: {id}}),
      content: <Interests maximization={maximization} />,
      'data-testid': testIds.vehicles.detail('navigation-item-interests'),
      badge: toMenuBadge(relatedRecordCount?.interests ?? 0),
    })
    .when(isSaleVehicle && isSalesModuleEnabled, {
      id: 'test-drives',
      label: i18n.t('entity.testDrive.labels.testDrives'),
      href: composePath(vehiclesRoutes.testDrives, {params: {id}}),
      content: isFeatureEnabled(featureFlags.SALES_TEST_DRIVES_V2) ? (
        <TestDrivesV2 />
      ) : (
        <TestDrives />
      ),
      isDisabled:
        (isFeatureEnabled(featureFlags.SALES_TEST_DRIVES_V2) && !hasReadTestDrivePermission) ||
        false,
      'data-testid': testIds.vehicles.detail('navigation-item-test-drive'),
      badge: toMenuBadge(relatedRecordCount?.testDrives ?? 0),
    })
    .when(
      isSaleVehicle &&
        hasVehicleReadActiveReservationPermission &&
        hasVehicleReadReservationHistoryPermission &&
        isSalesModuleEnabled,
      {
        id: 'reservations',
        label: i18n.t('entity.vehicle.labels.reservations'),
        href: composePath(vehiclesRoutes.reservations, {params: {id}}),
        content: <Reservations />,
        'data-testid': testIds.vehicles.detail('navigation-item-reservations'),
        badge:
          vehicleReservationStatus !== 'pending' &&
          !(isVehicleReservationError && is404ApiError(vehicleReservationError))
            ? {
                icon: 'navigation/check',
                colorScheme: 'green',
              }
            : undefined,
      }
    )
    .when(isRentalVehicle && canReadRentalOrder, {
      id: 'rental-orders',
      label: i18n.t('module.rentalOrder.title'),
      href: composePath(vehiclesRoutes.rentalOrders, {params: {id}}),
      badge: toMenuBadge(relatedRecordCount?.rentalOrders ?? 0),
      content: (
        <Main data-testid={testIds.vehicles.rentalOrders()}>
          <RentalVehicleRentalOrders vehicleId={id} maximization={maximization} />
        </Main>
      ),
      'data-testid': testIds.vehicles.detail('navigation-item-rental-orders'),
    })
    .when(canReadCarisServiceRequests, {
      id: 'caris-service-requests',
      label: i18n.t('entity.carisServiceRequest.labels.title'),
      href: composePath(vehiclesRoutes.carisServiceRequests, {params: {id}}),
      content: <CarisServiceRequests vehicleId={id} maximization={maximization} />,
      'data-testid': testIds.vehicles.detail('navigation-item-caris-service-requests'),
    })
    .when(
      isSalesModuleEnabled &&
        isSaleVehicle &&
        saleVehicle?.id &&
        (hasReadBusinessCasePurchasePermission || hasReadBusinessCaseSellingPermission),
      {
        id: 'business-cases',
        label: i18n.t('entity.vehicle.labels.businessCases'),
        href: composePath(vehiclesRoutes.businessCases, {params: {id}}),
        content: <BusinessCases />,
        badge: toMenuBadge(relatedRecordCount?.businessCases ?? 0),
        'data-testid': testIds.vehicles.detail('navigation-item-business-cases'),
      }
    )
    .when(hasComplaintReadPermission && isSaleVehicle && isSalesModuleEnabled, {
      id: 'vehicle-complaints',
      label: i18n.t('entity.vehicle.labels.complaints'),
      href: composePath(vehiclesRoutes.complaints, {params: {id}}),
      content: <Claims />,
      badge: isPositive(vehicleComplaints?.length)
        ? {
            value: vehicleComplaints?.length ?? 0,
            colorScheme: 'neutral',
          }
        : undefined,
      'data-testid': testIds.vehicles.detail('complaints'),
      hasSeparator: !isWorkshopModuleEnabled,
    })
    .when(
      isWorkshopModuleEnabled && isServiceVehicle,
      buildObject<NavigationItem>()
        .id('postponed-jobs')
        .label(i18n.t('entity.vehicle.labels.postponedJobs'))
        .href(composePath(vehiclesRoutes.postponedJobs, {params: {id}}))
        .content(<VehiclePostponedJobs vehicleId={id} />)
        .badge(
          {value: String(postopnedJobsData?.count ?? ''), colorScheme: 'neutral'},
          Boolean(postopnedJobsData?.count)
        )
        .build()
    )
    .when(isWorkshopModuleEnabled && isServiceVehicle, {
      id: 'ownership',
      label: i18n.t('entity.serviceCase.labels.ownership'),
      href: composePath(vehiclesRoutes.ownership, {params: {id}}),
      content: (
        <OwnershipSection vehicleId={id} resourceId={EntityResourceIds.vehicle} recordId={id} />
      ),
      badge: toMenuBadge(vehicleCustomers?.length ?? 0),
      'data-testid': testIds.vehicles.detail('navigation-item-ownership'),
    })
    .when(isWorkshopModuleEnabled && isServiceVehicle && canReadServiceCase, {
      id: 'activeServiceCases',
      label: i18n.t('entity.vehicles.labels.activeServiceCases'),
      href: composePath(vehiclesRoutes.activeServiceCases, {params: {id}}),
      content: <VehicleActiveServiceCases vehicleId={id} maximization={maximization} />,
      badge: isPositive(activeServiceCasesCount?.count)
        ? {value: String(activeServiceCasesCount?.count ?? ''), colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.vehicles.detail('navigation-item-activeServiceCases'),
    })
    .when(isWorkshopModuleEnabled && isServiceVehicle && canReadServiceCase, {
      id: 'historyOfRepairs',
      label: i18n.t('entity.serviceCase.labels.historyOfRepairs'),
      href: composePath(vehiclesRoutes.historyOfRepairs, {params: {id}}),
      content: [
        {
          route: vehiclesRoutes.historyOfRepairs,
          element: <VehicleHistoryOfRepairs vehicleId={id} maximization={maximization} />,
        },
      ],
      badge: toMenuBadge(finishedServiceCasesCount?.count ?? 0),
      'data-testid': testIds.vehicles.detail('navigation-item-historyOfRepairs'),
    })
    .when(isWorkshopModuleEnabled && isServiceVehicle, {
      id: 'serviceCampaigns',
      label: i18n.t('entity.campaigns.labels.serviceCampaigns'),
      href: composePath(vehiclesRoutes.serviceCampaigns, {params: {id}}),
      badge: {value: activeServiceCampaignsLength, colorScheme: 'neutral'},
      content: <Campaigns vehicleId={id} />,
      'data-testid': testIds.vehicles.detail('navigation-item-serviceCampaigns'),
    })
    .when(isTireInventoryEnabled, {
      id: 'tires',
      label: i18n.t('page.tiresInventory.labels.tires'),
      href: composePath(vehiclesRoutes.tires, {params: {id}}),
      content: <Tires maximization={maximization} />,
      badge: isPositive(vehicleTireSets?.count)
        ? {value: String(vehicleTireSets?.count ?? ''), colorScheme: 'neutral'}
        : undefined,
      isDisabled: !vehicleTires?.count,
      'data-testid': testIds.vehicles.detail('navigation-item-tires'),
      hasSeparator: true,
    })
    .add({
      id: 'activityLogs',
      label: i18n.t('general.labels.activityLogs'),
      href: composePath(vehiclesRoutes.activityLog, {params: {id}}),
      content: [
        {
          route: vehiclesRoutes.activityLog,
          element: <VehicleActivityLogs maximization={maximization} />,
        },
        {
          route: vehiclesRoutes.emailActivityLogs,
          element: <VehicleActivityLogs maximization={maximization} />,
        },
      ],
      'data-testid': testIds.vehicles.detail('navigation-item-activityLogs'),
    });

  if (!isVehicleLoading && is404ApiError(vehicleError)) {
    return <ErrorPage message={i18n.t('page.vehicle.notifications.notFound')} />;
  }

  return (
    <>
      <div ref={assistantEntitiesOwnerRef} hidden>
        <span ref={synchronizeAssistantEntitiesRef} aria-hidden />
      </div>
      <DataStatus isLoading={isVehicleLoading} isError={isVehicleErrored} minHeight={100}>
        <Show when={isNotNil(vehicleData) && !isVehicleLoading}>
          <VehicleWarehouseProvider>
            <Page
              key={id}
              navigation={navigationItems}
              header={<VehicleHeader vehicleId={id} data-testid={testIds.vehicles.detail()} />}
              activityPanel={<VehicleActivityPanel vehicleId={id} />}
              data-testid={testIds.vehicles.detail()}
            />
          </VehicleWarehouseProvider>
        </Show>
      </DataStatus>
    </>
  );
}
